/* eslint-disable arrow-body-style */
import { getSelectorConfig } from '@/pages/benchmark/services/benchmark';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl, useParams } from 'umi';
import { TFilterSelectorProps, TInputListItem, TSelectorDataProps, TSelectorItem } from './_type';
import { useFilterList } from './useFilterList';
import { LayoutContext } from '@/pages/benchmark/state/LayoutContext';
import { FRAME_TYPE_MAP, NO_OPTIONS_FILTER_TYPE_ARR, STRING_DEFAULT_FILTER_TYPE } from '@/pages/benchmark/_config/constant';
import { getCountryFlag } from '@home/_config/countries';
import TextBubbles from '@home/components/TextBubbles';

// 多选级联筛选器下拉过滤
const filterCascadeOptions = (data: any[], filterData: string[]) => {
  return data.map(item => ({
    ...item,
    list: item.list.filter((listItem: { code: string; }) => filterData.includes(listItem.code))
  })).filter(item => item.list.length > 0);
};

// 获取默认值
const getDefaultVal = (defaultVal: any[], viewType: string) => {
  if (viewType === 'cascade') {
    // 本次需求只有国家筛选器的后端默认值配置处理，这里先预先对级联筛选器进行扩展,range类型的扩展需要后端协议支持，本次先不扩展
    // 当前cascade类型的只有品类游戏筛选器，后期如果添加其他筛选器，注意genre和game的格式转换
    return {
      genre: [],
      subgenre: defaultVal,
      type: 'subgenre',
    };
  }
  const defaultIsString = STRING_DEFAULT_FILTER_TYPE?.includes(viewType);
  return defaultIsString ? defaultVal?.[0] : defaultVal;
};

export const useFilterSelector = (props: TFilterSelectorProps) => {
  const { api } = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  // const [selectorList, setSelectorList] = useState<{ [key: string]: Array<TSelectorItem> | boolean; }>({});
  const [selectorData, setSelectorData] = useState<TSelectorDataProps>({
    metric_list: [],
    selector: {},
  });

  const { frameType } = useContext(LayoutContext);
  const { metric } = useParams<{ metric: string; }>();
  // filterStaticList
  const { filterConfigList } = useFilterList(frameType);
  const { formatMessage, locale } = useIntl();

  const { selectorList, visibleSelector } = useMemo(() => {
    const selector = selectorData?.selector;
    if (!selector) return { selectorList: [], visibleSelector: {} };
    // 获取与当前 metric 匹配的 filter 列表
    const filterList = selectorData?.metric_list?.find((item) => item?.metric === metric)?.filter || [];
    // 过滤出可见的 filter 列表
    const visibleFilterList = filterList?.filter((item: { visible: any; }) => item?.visible) || [];
    // 如果没有可见的 filter 列表，直接返回空对象
    if (visibleFilterList.length === 0) {
      return { selectorList: [], visibleSelector: {} };
    }
    const visibleSelector: any = {};
    visibleFilterList.forEach((item: { name: string | number; }) => {
      visibleSelector[item?.name] = item;
    });
    // 构建最终的对象
    const result: { [key: string]: any; } = {};
    const keys = Object.keys(selector);
    for (const key of keys) {
      const baseKey = key.split('_list')[0];
      const visibleItem = visibleSelector?.[baseKey];
      if (visibleItem) {
        const visibleOptionsArr = visibleItem?.options;
        const viewType = visibleItem?.view_type;
        const selectorList = selector?.[key];
        if (visibleOptionsArr?.length > 0) {
          // 本次需求只有国家筛选器，这里先预先对级联筛选器进行扩展
          if (viewType === 'cascade') {
            const visibleOptions = filterCascadeOptions(selectorList, visibleOptionsArr);
            result[key] = visibleOptions;
          } else {
            const visibleOptions = selectorList?.filter(item => visibleOptionsArr?.includes(item?.code));
            result[key] = visibleOptions;
          }

        } else {
          result[key] = selector[key];
        }

      }
    }
    return {
      selectorList: result,
      visibleSelector
    };
  }, [selectorData, metric]);

  // 国家筛选器格式化下拉选项
  const formatCountry = useCallback((options: Array<TSelectorItem>): Array<TSelectorItem> => {
    // 查找 code 为 'global' 的元素
    const globalCountry: any = options.find(item => item.code === 'global');

    // 如果找到了 global 元素,如果没找到则不用处理
    if (globalCountry) {
      // 从原数组中移除 global 元素
      const index = options.indexOf(globalCountry);
      options.splice(index, 1);
      // 添加 info 属性
      globalCountry.info = formatMessage({ id: 'dbb_country_all' });
      // 将带有 info 属性的 global 元素添加到数组的开头
      options.unshift(globalCountry);
    }
    options = options.map(item => {
      // 添加 icon 属性, global不添加图标
      item.icon = item.code !== 'global' ? getCountryFlag(item?.code) : undefined;
      return item;
    });
    return options;
  }, [locale]);


  // 假设您有一个函数来处理这种数据转换
  const formatGenreSubgenre = (data: TSelectorItem[]) => {
    return data.map(item => {
      // 如果有子列表，则递归处理子项
      if (item?.list && item?.list?.length > 0) {
        const transformedList = item.list.map(subItem => ({
          ...subItem,
          // 子级可能会重复，另外后端要求传参为父级+ 子级 subgenre_id 竖线|分割
          // 如：("Action:Action-AdventurelcombatlOpen worldlstealthlstory RichlsuryivallThird-Person shooter".)
          code: `${item.code}:${subItem.code}` // 更新子项的code
        }));
        return { ...item, list: transformedList };
      }
      return item;
    });
  };

  const formatGame = (data: TSelectorItem[]) => {
    return data.map((item: TSelectorItem) => {
      return {
        ...item,
        render: (item: TSelectorItem) => {
          return <TextBubbles
            className="dbb-selector-optinons-show-tooltips"
            text={
              <div
                className="dbb-selector-optinons-show-tooltips"
                dangerouslySetInnerHTML={{ __html: item?.text || '' }}
              />
            }
          />;
        }
      };
    });
  };

  // 格式化下拉选项
  const formartSelectorData = useCallback((options: TSelectorItem[], key: string) => {
    if (key === 'country') {
      return formatCountry(options);
    }
    if (key === 'genre_subgenre') {
      return formatGenreSubgenre(options);
    }
    if (key === 'game') {
      return formatGame(options);
    }
    return options;
  }, [formatCountry]);

  useEffect(() => {
    if (!frameType) return;
    setError(false);
    setLoading(true);
    getSelectorConfig({
      frame_type: FRAME_TYPE_MAP?.[frameType],
      // 传空返回所有的指标
      metrics: [metric],
      genre_selector_type: 'genre_subgenre',
      api,
    }).then((data) => {
      // setSelectorList(data?.selector || {});
      setSelectorData(data);
    }).catch((e) => {
      setError(true);
      console.log('useFilterSelector error', e);
    }).finally(() => {
      setLoading(false);
    });
  }, [metric, api, frameType]);

  // 筛选器配置和下拉等相关信息
  const inputList: TInputListItem[] = useMemo(() => {
    // 重要,和超哥确认后：
    // 1、筛选器不需要有配置默认值的功能；
    // 2、 筛选器不做名称的配置
    // 3、后端接口返回筛选器信息， 有对应的下拉信息，则展示筛选器，没有对应的下拉信息，或者下拉为空数组或者null则不显示
    // 4、2024-11-11 改动，后端协议：pcu和revenue筛选器，是没有下拉信息的，如果筛选器配置值为true，则展示
    // 5、2024-11-13 改动，后端协议：添加visible字段，用来控制显示筛选器是否展示
    // 这里前端先做扩展，添加nameCN， nameEN，default，view_type 等信息

    const result = Object.keys(selectorList || {})?.map((item: string) => {
      const selectorItem = selectorList?.[item];
      // 这里和后端约定好了格式为筛选器key+_list
      const key = item?.split('_list')?.[0];
      const filterConfig = filterConfigList?.[key];
      // eslint-disable-next-line prefer-const
      let { view_type: viewType, nameCN, nameEN, default: defaultValue, name } = filterConfig || {};
      const { view_type: visibleViewType, default: visibleDefault } = visibleSelector?.[key] || {};
      let options: TSelectorItem[] = [];
      const viewTypeVal = visibleViewType || viewType;
      if (!NO_OPTIONS_FILTER_TYPE_ARR.includes(viewType)) {
        options = formartSelectorData((selectorItem as Array<TSelectorItem>) || [], key);
      }
      const defalutVal = getDefaultVal(visibleDefault, viewTypeVal);

      if (key === 'country') {
        const defaultIsExist = options?.find(option => option?.code === defaultValue);
        defaultValue = defaultIsExist ? defaultValue : (options?.[0]?.code ? options?.[0]?.code : defaultValue);
      }

      return {
        ...(filterConfig || {}),
        key,
        type: key,
        dataKey: item,
        name_cn: nameCN || key,
        name_en: nameEN || key,
        name: name || key,
        // 后端有传默认值则使用后端的默认值，否则直接使用前端的
        default: visibleDefault?.length > 0 ? defalutVal : defaultValue,
        view_type: viewTypeVal,
        // 经过上方selectorList的处理，当前selectorList中visible都为true
        visible: 1,
        options,

      };
    });
    return result;
  }, [selectorList, visibleSelector, filterConfigList]);

  return {
    loading,
    error,
    inputList,
    selectorList,
  };
};
